import {isTextAnnotation} from './annotationAnchors';
import {isQuestionRegionId, questionRegionRank} from './annotationRegions';
import {filterNotedAnnotations} from './annotationSelectors';
import type {Annotation, TextAnnotation} from './annotationTypes';

/** A noted mark with the bucket it lives in, which addressing the store needs. */
export interface QuestionNote {
  regionId: string;
  annotation: TextAnnotation;
}

/**
 * Every noted mark on `questionId`, in reading order: region tier, then text
 * position (stable, so equal-rank regions keep store order). Allocates — call
 * from a `useMemo`, never a zustand selector (see `filterNotedAnnotations`).
 *
 * Whole-supplement marks are left out: their region is question-free, and they
 * have no text to quote or order by. Listing them is a follow-up.
 */
export function collectQuestionNotes(
  annotationsByRegionId: Record<string, Annotation[]>,
  questionId: string | undefined
): QuestionNote[] {
  if (!questionId) {
    return [];
  }
  return Object.entries(annotationsByRegionId)
    .filter(([regionId]) => isQuestionRegionId(regionId, questionId))
    .sort(([a], [b]) => questionRegionRank(a) - questionRegionRank(b))
    .flatMap(([regionId, annotations]) =>
      // The margin peek's comparator (annotationNoteLines.ts), so orders agree.
      filterNotedAnnotations(annotations)
        .filter(isTextAnnotation)
        .sort((a, b) => a.anchor.start - b.anchor.start)
        .map((annotation) => ({regionId, annotation}))
    );
}
